<script lang="ts" setup>
import { computed, ref } from 'vue'

import { SmrStaySuggestionItem, SmrStaySuggestionWildcardChild, SuggestionJustification } from '@/misc/types'
import MedicalActForm from '@/stay-displayer/medical-act/MedicalActForm.vue'
import { sortSuggestedMedicalActs } from '@/stay-displayer/medical-act/sortSuggestedMedicalActs'
import { MedicalActSuggestion } from '@/stay-displayer/medical-act/types'
import SuggestedMedicalAct from '@/stay-displayer/smr/rhs/medical-act/SuggestedMedicalAct.vue'
import { PredictedLabelType, PredictedMedicalAct } from '@/store/modules/diagnosis/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { Rhs } from '@/store/modules/stay/smr/types'
import { StayHealthData } from '@/store/modules/stay/types'

const props = withDefaults(defineProps<{
  rhs: Rhs
  healthData: StayHealthData
  // Optional: fetched separately from the main stay payload and merged in once it resolves.
  staySuggestions?: SmrStaySuggestionItem[]
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>(), {
  staySuggestions: () => [],
  suggestionJustifications: null,
})

const codedActRefs = computed(() =>
  props.rhs.codedMedicalActs
    .filter((act) => !act.disabled)
    .map((act) => act.associatedAct.reference)
)

// The backend aggregates wildcard-matched searches on the parent item, not on each wildcard child
// (a real act matched only via the wildcard pattern has its own `searches` empty — see
// SuggestionRepository::getSuggestionsMcoForStay's searches_agg, grouped by the wildcard parent
// reference) — every child is suggested by the parent's searches plus any of its own.
function realSuggestionItems(item: SmrStaySuggestionItem): (SmrStaySuggestionItem | SmrStaySuggestionWildcardChild)[] {
  if (!item.isWildcard) {
    return [item]
  }
  return item.wildcardChildren.map((child) => {
    const searchesById = new Map([...item.searches, ...child.searches].map((search) => [search.sancareId, search]))
    return { ...child, searches: Array.from(searchesById.values()) }
  })
}

// SMR predictions are not populated by the backend yet (FT_CODAGE-331: every staySuggestions item's
// `predictions` stays empty for SMR). Existence is still checked against staySuggestions rather than
// rhs.predictedLabels directly, so this activates with no further frontend change the day the
// backend implements it — hydration goes through rhs.predictedLabels since a suggestion item's
// `predictions` don't carry the act description needed to render the card.
function resolvePredictedAct(reference: string): PredictedMedicalAct | null {
  return (props.rhs.predictedLabels.find((label) => label.type === PredictedLabelType.MEDICAL_ACTS && label.medicalAct?.reference === reference) as PredictedMedicalAct | undefined) ?? null
}

const visibleSuggestedActs = computed<MedicalActSuggestion[]>(() => {
  const result: MedicalActSuggestion[] = []

  for (const item of props.staySuggestions) {
    if (item.type !== 'medicalActs') {
      continue
    }

    for (const real of realSuggestionItems(item)) {
      if (codedActRefs.value.includes(real.reference)) {
        continue
      }

      const prediction = real.predictions.length ? resolvePredictedAct(real.reference) : null

      if (real.searches.length) {
        result.push({
          reference: real.reference,
          description: real.description ?? '',
          searches: real.searches.map((search) => ({ search: { id: search.sancareId, name: search.name } })),
          prediction,
        })
      } else if (prediction) {
        result.push({ reference: real.reference, description: prediction.medicalAct.description, searches: [], prediction })
      }
    }
  }

  return sortSuggestedMedicalActs(result)
})

const actFormVisible = ref(false)
const preSelectedAct = ref<{ reference: string, description: string } | null>(null)

function promoteMedicalAct(suggestion: MedicalActSuggestion) {
  preSelectedAct.value = { reference: suggestion.reference, description: suggestion.description }
  actFormVisible.value = true
}
</script>

<template>
  <div class="dashboard-title">
    <small>{{ $t('medicalAct.title', 2) }} - {{ $t('stayDisplayer.sancareSuggestion', visibleSuggestedActs.length) }}</small>
  </div>
  <div v-if="visibleSuggestedActs.length === 0">
    <small class="aligned-row fst-italic">{{ $t('medicalAct.suggestedNone') }}</small>
  </div>
  <ul
    v-else
    class="list-unstyled p-0 m-0"
  >
    <li
      v-for="(suggestion, index) in visibleSuggestedActs"
      :key="suggestion.reference"
      class="px-2 py-1 hoverable-item"
    >
      <suggested-medical-act
        :health-data="healthData"
        :suggestion="suggestion"
        :suggestion-justifications="suggestionJustifications"
        @promote-medical-act="promoteMedicalAct"
      />
    </li>
  </ul>
  <medical-act-form
    :summary-unit="rhs"
    :pmsi-type="PmsiTypeEnum.SMR"
    :presence-days="rhs.weekDays + rhs.weekEndDays"
    :pre-selected-medical-act="preSelectedAct"
    :visible="actFormVisible"
    @close-form="actFormVisible = false"
  />
</template>
